<script lang="ts">
/**
 * @deprecated Gebruik het nieuwe component `FileInput`.
 *
 * @example
 * ```vue
 * <FileInput v-model="files" :id="generatedUniqueId" multiple />
 * ```
 */
</script>

<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import FadeSlideTransition from '@shared/animation/transitions/FadeSlideTransition/FadeSlideTransition.vue';
import { getFontAwesomeIconFromMimeType } from '@shared/plugin/icons';
import { useNotification } from '@shared/plugin/prime-vue/useNotification';
import { parseArrayAsEnumerationString } from '@tools/parsers';
import { computed, reactive, useTemplateRef } from 'vue';
import useUploadFieldBase from '../UploadFieldBase/composables/uploadFieldBase';

export interface FileType {
  extension: string;
  mimeType: string;
}

export interface UploadFieldProps {
  allowedFileTypes?: FileType[];
  allowRemove?: boolean;
  disabled?: boolean;
  hideAllowedFileTypes?: boolean;
  id?: string;
  maxSize?: number;
  multiple?: boolean;
  path?: string;
  readonly?: boolean;
  value?: File[];
  /**
   * Nodig wanneer het veld hidden is en de gebruiker de input niet kan resetten.
   * Zonder deze te zetten kun je niet twee keer achter elkaar een bestand met dezelfde naam/grootte uploaden.
   * @type {Boolean}
   * @default false
   */
  resetInputValue?: boolean;
  download?: boolean;
}

const props = withDefaults(defineProps<UploadFieldProps>(), {
  allowRemove: true,
  allowedFileTypes: undefined,
  maxSize: 20 * 1024 * 1024,
});

const emit = defineEmits<{
  'update:value': [value: File[]];
  updateField: [valuePath: { path?: string; value: File[] }];
}>();

const state = reactive({
  dragging: false,
});

const inputRef = useTemplateRef('input');
const notification = useNotification();
const { checkSize } = useUploadFieldBase(() => props.maxSize);

function checkFiles(files: File[]) {
  const checkedFiles: File[] = [];

  files.forEach((file) => {
    if (checkType(file) && checkSize(file)) {
      checkedFiles.push(file);
    }
  });

  return checkedFiles;
}

function checkType(file: File) {
  if (!props.allowedFileTypes?.length) return true;

  const isAllowed = props.allowedFileTypes.some(
    (fileType) =>
      file.type === fileType.mimeType ||
      file.name.toLowerCase().endsWith(fileType.extension.toLowerCase()),
  );

  if (!isAllowed) {
    const extensions = props.allowedFileTypes.map((type) => type.extension);
    notification.error(
      `Bestandstype van ${file.name} wordt niet geaccepteerd. Upload een bestand van het type ${parseArrayAsEnumerationString(extensions, 'of')}.`,
    );
  }

  return isAllowed;
}

function updateValue(files: File[]) {
  if (props.disabled || !files.length) {
    if (inputRef.value) {
      inputRef.value.value = '';
    }

    emit('update:value', []);
    return;
  }

  const checkedFiles = checkFiles(props.multiple ? files : files.toSpliced(1, 1));

  emit('updateField', {
    path: props.path,
    value: checkedFiles,
  });

  emit('update:value', checkedFiles);

  if (!checkedFiles.length && inputRef.value) {
    inputRef.value.value = '';
  }
}

function handleChange({ target }: Event) {
  const { files } = target as HTMLInputElement;

  updateValue(files ? Array.from(files) : []);

  if (props.resetInputValue) {
    (target as HTMLInputElement).value = '';
  }
}

const acceptAttribute = computed(
  () =>
    props.allowedFileTypes
      ?.map((type) => [type.extension, type.mimeType])
      .flat()
      .join(',') || undefined,
);

const activate = () => {
  if (!inputRef.value) {
    return;
  }

  inputRef.value.click();
};

function getDownloadUrl(file: File) {
  return URL.createObjectURL(new Blob([file], { type: file.type }));
}

defineExpose({ activate });
</script>

<template>
  <div class="grid w-fit leading-tight text-grey-700">
    <div
      :class="{
        'pointer-events-none': !props.value?.length,
        'z-10 col-start-1 row-start-1': !props.multiple,
        flex: !props.readonly || (!props.multiple && props.readonly),
      }"
    >
      <FadeSlideTransition
        :class="[
          '*:!duration-500',
          {
            grid: !props.multiple,
            'w-0 grow': !props.readonly || (!props.multiple && props.readonly),
          },
        ]"
        direction="right"
        group
        tag="ul"
      >
        <li
          v-for="({ name, type }, index) in props.value"
          :aria-label="name"
          :class="[
            'flex cursor-default items-center gap-2 rounded-lg border border-grey-200 bg-grey-100 px-4 py-[.6875rem] transition-transform has-[.custom-has-reference--delete-button:hover]:-translate-x-1.5',
            {
              'cursor-not-allowed': props.disabled,
              'min-w-0': !props.multiple,
              'mb-2.5': props.multiple,
            },
          ]"
          :key="name"
        >
          <FontAwesomeIcon
            class="[--fa-fw-width:_1rem]"
            fixed-width
            :icon="['far', getFontAwesomeIconFromMimeType(type)]"
          />

          <a
            v-if="props.download && props.value"
            aria-label="Downloaden"
            class="me-2 truncate text-primary hover:underline"
            :href="getDownloadUrl(props.value[index])"
            :download="name"
            title="Download bestand"
          >
            {{ name }}
          </a>

          <span v-else class="truncate">
            {{ name }}
          </span>

          <div class="ms-auto">
            <button
              v-if="props.allowRemove && !props.disabled && !props.readonly"
              aria-label="Verwijderen"
              class="custom-has-reference--delete-button -my-3 -me-4 -ms-2 py-3 pe-4 ps-2 text-red transition-transform duration-500 hover:translate-x-1.5"
              type="button"
              @click="updateValue(props.value?.toSpliced(index, 1) ?? [])"
            >
              <FontAwesomeIcon :icon="['far', 'trash']" />
            </button>
          </div>
        </li>
      </FadeSlideTransition>
    </div>

    <div
      v-if="!props.readonly || (props.value?.length && !props.multiple && props.readonly)"
      :class="[
        'relative grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1 rounded-lg border border-dashed px-4 py-3',
        {
          'transition-colors': !props.disabled,
          'cursor-not-allowed bg-grey-50 text-grey-400': props.disabled,
          'bg-white': !props.disabled && !state.dragging,
          'border-grey-300': props.disabled || !state.dragging,
          'border-primary bg-primary-200': !props.disabled && state.dragging,
          'col-start-1 row-start-1': !props.multiple,
        },
      ]"
    >
      <input
        :accept="acceptAttribute"
        :aria-label="`Drag & Drop of kies ${!props.multiple ? 'een ' : ''}bestand${props.multiple ? 'en' : ''}`"
        :class="[
          'absolute inset-0 cursor-pointer opacity-0',
          {
            'pointer-events-none': props.disabled,
          },
        ]"
        :disabled="props.disabled"
        :id="props.id"
        :multiple="props.multiple"
        ref="input"
        type="file"
        @change="handleChange"
        @dragenter="state.dragging = true"
        @dragleave="state.dragging = false"
        @drop.stop="state.dragging = false"
      />

      <FontAwesomeIcon class="[--fa-fw-width:_1rem]" fixed-width :icon="['far', 'file-upload']" />

      <span class="grid">
        <span
          :class="[
            'col-start-1 row-start-1',
            {
              invisible: !props.disabled && state.dragging,
            },
          ]"
        >
          Drag & Drop of

          <strong
            :class="{
              'text-primary': !props.disabled,
              'font-normal': props.disabled,
            }"
          >
            {{ `kies ${!props.multiple ? 'een ' : ''}bestand${props.multiple ? 'en' : ''}` }}
          </strong>
        </span>

        <strong
          :class="[
            'col-start-1 row-start-1 text-primary',
            {
              invisible: props.disabled || !state.dragging,
            },
          ]"
        >
          Drop bestand<template v-if="props.multiple">en</template> om te uploaden...
        </strong>
      </span>

      <footer
        v-if="props.allowedFileTypes && !props.hideAllowedFileTypes"
        class="col-start-2 text-sm text-grey-400"
      >
        <template v-if="props.allowedFileTypes.length">
          {{
            parseArrayAsEnumerationString(
              props.allowedFileTypes.map((type) => type.extension.replace('.', '')),
              'of',
            )
          }}
        </template>
        <template v-else> &nbsp; </template>
      </footer>
    </div>
  </div>
</template>
